/**
 * MOCK REPLAY MODE — activation flag (frontend-only, zero backend calls).
 *
 * LOCAL DEBUG ONLY: the replay is a developer tool, never a user-facing
 * feature — it activates ONLY in a dev build (import.meta.env.DEV). In a
 * production build the `?mock=aditya` param is inert and the mock machinery
 * (including its bundled fixture) is dead code the bundler can tree-shake, so
 * the mock page can never be reached in a deployed environment.
 *
 * The drafting route enters mock replay when a DEV page URL carries
 * `?mock=aditya`, e.g.:
 *
 *     http://localhost:5173/drafting?mock=aditya
 *
 * The query string is read ONCE and latched for the lifetime of the page:
 * in-app navigation can rewrite the URL, and several call sites branch on the
 * flag in ways that must never flap mid-session (useOIOAgent swaps its hook
 * implementation, DraftingRoute swaps its whole tree, GenerationTracker goes
 * inert). A full page load with the param present is the only way in; a full
 * page load without it is the only way out.
 *
 * When the flag is OFF, none of the mock machinery executes and the real
 * drafting path behaves exactly as before.
 */

/**
 * The synthetic, client-side-only case id the replay runs under. Never sent to
 * the backend, never appears in the real case list/Vault — it exists only in
 * the mock ThreadContext override and the per-case localStorage keys (which
 * the replay clears on start/reset).
 */
export const MOCK_THREAD_ID = "mock-aditya"

let latched: boolean | null = null

export function isMockReplay(): boolean {
  if (latched === null) {
    latched =
      import.meta.env.DEV &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("mock") === "aditya"
  }
  return latched
}

/**
 * Test-only escape hatch: vitest files share module state, so tests force the
 * latch instead of mutating window.location. Pass null to re-arm the lazy read.
 */
export function __setMockReplayForTests(value: boolean | null): void {
  latched = value
}

/** "Aditya Eden Woods (mock replay of a real run)" → "Aditya Eden Woods". */
export function shortCaseLabel(caseLabel: string): string {
  return caseLabel.replace(/\s*\([^)]*\)\s*$/, "").trim() || caseLabel
}
